import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, ChevronLeft, Home, MapPin, Search, Sparkles } from "lucide-react";
import api from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { breadcrumbLd, propertyPath, useSeo } from "@/lib/seo";
import { publicUrl } from "@/lib/publicUrl";

export default function SeoLandingPage({ slug }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/seo/landing/${slug}`)
            .then((response) => setData(response.data))
            .catch(() => { setData(null); setNotFound(true); })
            .finally(() => setLoading(false));
        window.scrollTo(0, 0);
    }, [slug]);

    useSeo({
        title: data?.seo?.title || "Ricerca immobiliare | VendiDirect",
        description: data?.seo?.description,
        canonical: `/${slug}`,
        image: data?.items?.find((item) => item.photos?.[0])?.photos?.[0],
        jsonLd: data ? [
            breadcrumbLd([
                { name: "Home", path: "/" },
                { name: "Annunci", path: "/annunci" },
                { name: data.seo.h1, path: `/${slug}` },
            ]),
            {
                "@type": "CollectionPage",
                name: data.seo.h1,
                description: data.seo.description,
                url: publicUrl(`/${slug}`),
                mainEntity: {
                    "@type": "ItemList",
                    numberOfItems: data.count,
                    itemListElement: data.items.map((item, index) => ({
                        "@type": "ListItem", position: index + 1,
                        name: item.title, url: publicUrl(propertyPath(item)),
                    })),
                },
            },
        ] : null,
    }, [data, slug, notFound]);

    if (loading) return <div data-testid="seo-landing-loading" className="pt-32 pb-24 text-center text-ink-400">Preparazione della guida locale…</div>;
    if (!data) {
        return (
            <main data-testid="seo-landing-not-found" className="pt-32 pb-24 px-6 text-center">
                <h1 className="font-serif text-4xl text-ink-50">Ricerca non disponibile</h1>
                <Link data-testid="seo-landing-not-found-link" to="/annunci" className="cta-gold inline-flex mt-8 px-6 py-3">Esplora gli annunci</Link>
            </main>
        );
    }

    return (
        <main data-testid="seo-landing-page" className="pt-28 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <Link data-testid="seo-landing-back" to="/annunci" className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-gold transition-colors mb-8">
                <ChevronLeft size={14} /> Tutti gli annunci
            </Link>

            <header className="max-w-5xl mb-12">
                <div data-testid="seo-landing-location" className="flex items-center gap-2 text-xs text-ink-400 uppercase mb-4">
                    <MapPin size={14} className="text-gold" /> {data.city}{data.region ? ` · ${data.region}` : ""}
                </div>
                <h1 data-testid="seo-landing-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink-50 leading-tight">
                    {data.seo.h1}
                </h1>
                <div className="flex flex-wrap gap-3 mt-6">
                    <span data-testid="seo-landing-subject" className="neu-btn px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><Home size={14} /> {data.subject}</span>
                    {data.feature && <span data-testid="seo-landing-feature" className="neu-btn px-4 py-2 text-sm font-medium">{data.feature}</span>}
                    <span data-testid="seo-landing-count" className="neu-btn px-4 py-2 text-sm font-medium">{data.count} annunci</span>
                </div>
            </header>

            <section data-testid="seo-landing-ai-guide" className="news-card-neu p-6 md:p-8 mb-14 max-w-5xl">
                <div className="flex items-center gap-2 text-[11px] uppercase font-bold text-blue-700 mb-3">
                    <Sparkles size={14} /> Guida immobiliare locale
                </div>
                <p className="text-base md:text-lg leading-relaxed" style={{ color: "#1E293B" }}>{data.guide}</p>
            </section>

            <section data-testid="seo-landing-results">
                <span className="label-eyebrow">Immobili disponibili</span>
                <div className="flex items-center justify-between gap-4 flex-wrap mt-2 mb-8">
                    <h2 className="font-serif text-base md:text-lg text-ink-50">{data.count ? `${data.count} opportunità per questa ricerca` : "Nuove opportunità in arrivo"}</h2>
                    <Link data-testid="seo-landing-catalog-link" to={data.catalog_url} className="cta-outline px-4 py-2 text-xs inline-flex items-center gap-2"><Search size={13} /> Modifica ricerca</Link>
                </div>
                {data.items.length > 0 ? (
                    <div data-testid="seo-landing-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-14">
                        {data.items.map((item, index) => <PropertyCard key={item.id} property={item} index={index} />)}
                    </div>
                ) : (
                    <div data-testid="seo-landing-empty" className="feature-card-neu p-8 md:p-10 max-w-4xl border border-gold/30">
                        <BellRing size={28} className="text-gold mb-5" />
                        <h2 className="font-serif text-base md:text-lg text-ink-50">Al momento non ci sono annunci in questa zona. Iscriviti per essere il primo a riceverli!</h2>
                        <p className="text-sm text-ink-400 mt-3 max-w-2xl">Crea il tuo profilo gratuito e salva la ricerca: ti avviseremo quando verrà pubblicato un immobile compatibile.</p>
                        <Link data-testid="seo-landing-empty-signup" to="/login" className="cta-gold inline-flex mt-6 px-6 py-3 text-sm">Iscriviti gratis</Link>
                    </div>
                )}
            </section>

            {data.related?.length > 0 && (
                <nav data-testid="seo-landing-related" aria-label="Ricerche immobiliari correlate" className="mt-24">
                    <span className="label-eyebrow">Ricerche correlate a {data.city}</span>
                    <div className="flex flex-wrap gap-3 mt-6">
                        {data.related.map((item) => (
                            <Link key={item.slug} data-testid={`seo-related-${item.slug}`} to={`/${item.slug}`} className="neu-btn px-4 py-2.5 text-sm font-medium">
                                {item.title}
                            </Link>
                        ))}
                    </div>
                </nav>
            )}
        </main>
    );
}